import React from 'react';
import { type TimelineEvent, getActionLabel, getActionColorCategory } from '@/hooks/useTimelineDecisoes';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ArrowRightLeft,
  CheckCircle2,
  XCircle,
  FileText,
  Clock,
  User,
  Trash2,
  MessageSquare,
  Upload,
  AlertTriangle,
  Hash,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/** Color tokens per action category */
const COLOR_CLASSES: Record<string, { badge: string; dot: string }> = {
  creation:   { badge: 'bg-blue-500/15 text-blue-400 border-blue-500/30',    dot: 'bg-blue-500 ring-blue-500/30' },
  alteration: { badge: 'bg-amber-500/15 text-amber-400 border-amber-500/30', dot: 'bg-amber-500 ring-amber-500/30' },
  approval:   { badge: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', dot: 'bg-emerald-500 ring-emerald-500/30' },
  rejection:  { badge: 'bg-red-500/15 text-red-400 border-red-500/30',       dot: 'bg-red-500 ring-red-500/30' },
  deletion:   { badge: 'bg-muted text-muted-foreground border-border',       dot: 'bg-muted-foreground ring-muted-foreground/30' },
  comment:    { badge: 'bg-purple-500/15 text-purple-400 border-purple-500/30', dot: 'bg-purple-500 ring-purple-500/30' },
};

const ACTION_ICONS: Record<string, React.ElementType> = {
  creation: FileText,
  alteration: ArrowRightLeft,
  approval: CheckCircle2,
  rejection: XCircle,
  deletion: Trash2,
  comment: MessageSquare,
  upload: Upload,
};

const TIPO_LABELS: Record<string, string> = {
  transicao: 'Transição',
  aprovacao: 'Aprovação',
  auditoria: 'Auditoria',
};

function formatDate(d: string) {
  try {
    return format(new Date(d), "dd MMM yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return d;
  }
}

interface Props {
  event: TimelineEvent;
  onSelect: (event: TimelineEvent) => void;
  isLast?: boolean;
}

export const TimelineEventCard: React.FC<Props> = ({ event, onSelect, isLast }) => {
  const colorCat = getActionColorCategory(event.acao);
  const colors = COLOR_CLASSES[colorCat] || COLOR_CLASSES.alteration;
  const Icon = ACTION_ICONS[colorCat] || FileText;

  return (
    <div className="relative flex gap-4 group">
      {/* Timeline dot + line */}
      <div className="flex flex-col items-center">
        <div className={`w-3.5 h-3.5 rounded-full ring-4 ${colors.dot} flex-shrink-0 mt-2 transition-transform group-hover:scale-125`} />
        {!isLast && <div className="w-px flex-1 bg-border/40 mt-1" />}
      </div>

      {/* Card */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Card
            className={`flex-1 mb-3 cursor-pointer border-border/50 hover:border-border transition-all hover:shadow-md ${event.is_critical ? 'ring-1 ring-amber-500/40' : ''}`}
            onClick={() => onSelect(event)}
          >
            <CardContent className="p-3.5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {/* Contract number - prominent */}
                  {event.contrato_numero && (
                    <div className="flex items-center gap-1.5 mb-1.5 text-xs font-semibold text-primary">
                      <Hash className="w-3.5 h-3.5" />
                      <span>{event.contrato_numero}</span>
                      {event.contrato_nome && (
                        <span className="font-normal text-muted-foreground">— {event.contrato_nome}</span>
                      )}
                    </div>
                  )}

                  {/* Tags */}
                  <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                    <Badge variant="outline" className={`text-xs ${colors.badge} border`}>
                      <Icon className="w-3 h-3 mr-1" />
                      {getActionLabel(event.acao)}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {TIPO_LABELS[event.tipo] || event.tipo}
                    </Badge>
                    {event.etapa_nome && (
                      <Badge variant="outline" className="text-xs">{event.etapa_nome}</Badge>
                    )}
                    {event.is_critical && (
                      <Badge variant="destructive" className="text-xs">
                        <AlertTriangle className="w-3 h-3 mr-0.5" />
                        Crítico
                      </Badge>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-sm font-medium text-foreground line-clamp-2">{event.descricao}</p>


                  {/* Meta */}
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground flex-wrap">
                    {event.usuario_nome && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {event.usuario_nome}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(event.created_at)}
                    </span>
                    {event.documento_ref && (
                      <span className="flex items-center gap-1 text-primary/70">
                        <FileText className="w-3 h-3" />
                        {event.documento_ref}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs">
          <p className="font-medium text-xs">{getActionLabel(event.acao)} — {TIPO_LABELS[event.tipo]}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{event.descricao}</p>
          {event.contrato_numero && <p className="text-xs mt-0.5">📋 Contrato {event.contrato_numero}</p>}
          {event.usuario_nome && <p className="text-xs mt-0.5">👤 {event.usuario_nome}</p>}
        </TooltipContent>
      </Tooltip>
    </div>
  );
};
